import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";
import {
  SUPPORTED_LANGUAGES,
  DICTIONARIES,
} from "@/lib/i18n";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "fm_language";
const DEFAULT_LANG = "en";

const I18nContext = createContext({
  language: DEFAULT_LANG,
  setLanguage: () => {},
  t: (k) => k,
  isAsl: false,
});

function normalize(lang) {
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANG;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return normalize(raw);
  } catch (_) {
    return DEFAULT_LANG;
  }
}

function applyDocumentAttrs(lang) {
  if (typeof document === "undefined") return;
  const isAsl = lang === "asl";
  // Language attribute reflects the actual TEXT language; ASL text is
  // English (accessibility overlay, not a translation).
  document.documentElement.lang = isAsl ? "en" : lang;
  document.documentElement.setAttribute(
    "data-a11y-mode",
    isAsl ? "asl" : "default"
  );
}

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [language, setLanguageState] = useState(() => readStoredLanguage());

  // Apply document attrs on mount + whenever language changes.
  useEffect(() => {
    applyDocumentAttrs(language);
  }, [language]);

  // When the user hydrates from /api/auth/me, prefer their stored server
  // preference. This overrides localStorage on first load so the language
  // survives a fresh browser (device change) once the user is logged in.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug(
      "[i18n] user hydration check → user.preferred_language=",
      user?.preferred_language,
      "current language=",
      language
    );
    if (user && user.preferred_language) {
      const backendLang = normalize(user.preferred_language);
      if (backendLang !== language) {
        // eslint-disable-next-line no-console
        console.debug(
          "[i18n] applying backend language",
          backendLang,
          "over local",
          language
        );
        setLanguageState(backendLang);
        try {
          window.localStorage.setItem(STORAGE_KEY, backendLang);
        } catch (_) {
          /* ignore */
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.preferred_language]);

  const setLanguage = useCallback(
    async (raw) => {
      const newLang = normalize(raw);
      const prev = language;
      // Optimistic UI + localStorage.
      setLanguageState(newLang);
      try {
        window.localStorage.setItem(STORAGE_KEY, newLang);
      } catch (_) {
        /* ignore quota */
      }

      // Persist to backend only if authenticated. Unauthenticated flow
      // (landing page) just updates localStorage + the document attrs.
      const token =
        typeof window !== "undefined"
          ? window.localStorage.getItem("figumatch_token")
          : null;
      if (!token) return;

      try {
        await api.patch("/auth/me/language", {
          preferred_language: newLang,
        });
      } catch (err) {
        // Revert on failure.
        setLanguageState(prev);
        try {
          window.localStorage.setItem(STORAGE_KEY, prev);
        } catch (_) {
          /* ignore */
        }
        const failedMsg =
          DICTIONARIES[prev]?.["alerts.language_update_failed"] ||
          DICTIONARIES.en["alerts.language_update_failed"];
        toast.error(failedMsg);
      }
    },
    [language]
  );

  const t = useCallback(
    (key, fallback) => {
      const dict = DICTIONARIES[language] || {};
      if (dict[key] != null) return dict[key];
      // Fall back to English.
      if (DICTIONARIES.en[key] != null) return DICTIONARIES.en[key];
      // Final fallback: caller-supplied fallback, then the key itself.
      // eslint-disable-next-line no-console
      console.warn("[i18n] missing key:", key);
      return fallback != null ? fallback : key;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      isAsl: language === "asl",
    }),
    [language, setLanguage, t]
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
